function parseDate(value: unknown): Date | undefined {
  if (value == null) {
    return undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (typeof value === "object" && "toString" in value) {
    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export function getMostRecentActionDate(
  actions: { date: Date | string }[],
): Date | undefined {
  if (actions.length === 0) {
    return undefined;
  }

  return actions.reduce((latest, action) => {
    const date = parseDate(action.date);
    if (!date) {
      return latest;
    }
    return date > latest ? date : latest;
  }, parseDate(actions[0].date) ?? new Date(0));
}

export function addYears(date: Date, years: number): Date {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
}

export type MaintenancePlanScheduleStatus =
  | { kind: "no-interval" }
  | {
      kind: "scheduled";
      baseDate: Date;
      nextActionDate: Date;
      actionInCurrentYear: boolean;
      nextActionOverdue: boolean;
    };

export function getMaintenancePlanSchedule({
  createdAt,
  yearlyFrequency,
  actions,
  now = new Date(),
}: {
  createdAt: Date | string | unknown;
  yearlyFrequency?: number | string | null;
  actions: { date: Date | string }[];
  now?: Date;
}): MaintenancePlanScheduleStatus {
  const intervalYears = Number(yearlyFrequency);
  if (yearlyFrequency == null || !Number.isFinite(intervalYears)) {
    return { kind: "no-interval" };
  }

  const planCreatedAt = parseDate(createdAt) ?? now;
  const mostRecent = getMostRecentActionDate(actions);
  const baseDate = mostRecent ?? planCreatedAt;
  const nextActionDate = addYears(baseDate, intervalYears);
  const currentYear = now.getFullYear();

  if (Number.isNaN(nextActionDate.getTime())) {
    return { kind: "no-interval" };
  }

  return {
    kind: "scheduled",
    baseDate,
    nextActionDate,
    actionInCurrentYear: mostRecent?.getFullYear() === currentYear,
    nextActionOverdue: nextActionDate.getFullYear() < currentYear,
  };
}

export function formatYearMonth(date: Date): string {
  const parsed = parseDate(date);
  if (!parsed) {
    return "-";
  }
  const year = parsed.getFullYear();
  const month = parsed.getMonth() + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function formatMaintenancePlanLastAction(
  lastActionDate: Date | string | undefined,
): string {
  if (!lastActionDate) {
    return "-";
  }
  const parsed = parseDate(lastActionDate);
  return parsed ? formatYearMonth(parsed) : "-";
}

export function getMaintenancePlanNextActionDate({
  createdAt,
  yearlyFrequency,
  lastActionDate,
}: {
  createdAt: Date | string | unknown;
  yearlyFrequency?: number | string | null;
  lastActionDate?: Date | string;
}): Date | undefined {
  const schedule = getMaintenancePlanSchedule({
    createdAt,
    yearlyFrequency,
    actions: lastActionDate ? [{ date: lastActionDate }] : [],
  });
  if (schedule.kind === "no-interval") {
    return undefined;
  }
  return schedule.nextActionDate;
}

export function formatMaintenancePlanNextAction(
  params: Parameters<typeof getMaintenancePlanNextActionDate>[0],
): string {
  const nextActionDate = getMaintenancePlanNextActionDate(params);
  return nextActionDate ? formatYearMonth(nextActionDate) : "-";
}
